import {
  createRootRoute,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { DevicesProviders } from '@src/providers/DevicesProviders'
import { ENV } from '@src/config/env'
import DevicesLayout from '@src/components/layout/DevicesLayout'
import NotFound from '@src/components/NotFound'
import ErrorBoundary from '@src/ErrorBoundry'
import { initDevicesSentry } from '@src/utils/Sentry/initSentry'
import Request from '@src/services/api/Request'
import AnalyticsPostHydrationBootstrap from '@src/utils/analytics/AnalyticsPostHydrationBootstrap'
import {
  buildDevicesGtmHeadScripts,
  DEVICES_GTM_NOSCRIPT_IFRAME_SRC,
} from '@src/utils/analytics/gtmBootstrap'
import { albertSansHeadLinks } from 'app-core'

export const Route = createRootRoute({
  loader: async () => {
    const [footer, navbar, products, formSettings] = await Promise.all([
      Request.footer(),
      Request.navbar(),
      Request.technologyPages(),
      // Pages still render, without forms, if Form Settings cannot be loaded.
      Request.formSettings().catch(() => null),
    ])

    return {
      footer: footer ?? null,
      navbar: navbar ?? null,
      products: products ?? null,
      formSettings,
    }
  },
  staleTime: 60_000,
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: NotFound,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'description', content: 'Healthxchange Devices' },
      { name: 'theme-color', content: '#FFFFFF' },
    ],
    title: 'Healthxchange Devices',
    links: [
      { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'manifest', href: '/site.webmanifest' },
      ...albertSansHeadLinks(),
    ],
    scripts: buildDevicesGtmHeadScripts(),
  }),
})

function formatRouteError(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'string' && error) {
    return error
  }
  try {
    return JSON.stringify(error, null, 2)
  } catch {
    return String(error)
  }
}

/** SSR 500s ship without client JS, so the default "Show Error" button cannot work. */
function RootError({ error }: ErrorComponentProps) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Healthxchange Devices</title>
      </head>
      <body>
        <main
          style={{ fontFamily: 'sans-serif', padding: '2rem', maxWidth: 720 }}
        >
          <h1>Something went wrong</h1>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {formatRouteError(error)}
          </pre>
        </main>
      </body>
    </html>
  )
}

function RootComponent() {
  const router = useRouter()
  const site = Route.useLoaderData()

  useEffect(() => {
    initDevicesSentry(router)
  }, [router])

  useEffect(() => {
    if (ENV.DEBUG) return
    const location = window.location
    if (
      !location.hostname.endsWith('healthxchangedevices.com') &&
      !location.hostname.endsWith('amplifyapp.com')
    ) {
      window.location.href = `https://www.healthxchangedevices.com${location.pathname}`
    }
  }, [])

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src={DEVICES_GTM_NOSCRIPT_IFRAME_SRC}
            height="0"
            width="0"
            title="Google Tag Manager"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        <AnalyticsPostHydrationBootstrap />
        <DevicesProviders>
          <DevicesLayout site={site}>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </DevicesLayout>
        </DevicesProviders>
        <Scripts />
      </body>
    </html>
  )
}
